import type { ICoord2D } from '@nova/types';

import { GeomUtils } from './geom-utils';

// TODO: Check if all usage is from Points and Vectors, eliminate these functions

/** Shared float tolerances for near-zero / near-equal checks. */
export const EPSILON_12 = 1e-12;
export const EPSILON_9 = 1e-9;
export const EPSILON_6 = 1e-6;
export const EPSILON_4 = 1e-4;
export const EPSILON_3 = 1e-3;

export function GetSqrMag(pointA: ICoord2D, pointB: ICoord2D): number {
  const deltaX = pointA.x - pointB.x;
  const deltaY = pointA.y - pointB.y;

  return (deltaX * deltaX) + (deltaY * deltaY);
}

// ! Avoid where possible
export function GetMag(pointA: ICoord2D, pointB: ICoord2D): number {
  return Math.sqrt(GetSqrMag(pointA, pointB));
}

const rad2DegMultiplier = 180 / Math.PI;

export function Rad2Deg(radians: number): number {
  return (radians * rad2DegMultiplier) + (radians > 0 ? 0 : 360);
}

/** Wrap degrees to [0, 360). */
export function normalizeAngleDeg(angleDeg: number): number {
  const normalized = angleDeg % 360;
  return normalized < 0 ? normalized + 360 : normalized;
}

/**
 * Angle in degrees [0, 360) from a direction with mathematical Y-up (x+east, y+north).
 * Uses atan2(y, x). Same convention as {@link Vector.GetDegreeAngle} when the vector is already Y-up.
 */
export function directionVectorToAngleDegYUp(dir: ICoord2D): number {
  return normalizeAngleDeg(Rad2Deg(Math.atan2(dir.y, dir.x)));
}

/**
 * Angle in degrees [0, 360) from a direction in engine space (x+east, y+south).
 * Uses atan2(-y, x). Not interchangeable with {@link directionVectorToAngleDegYUp} or
 * {@link Vector.GetDegreeAngle}, which assume Y-up.
 */
export function directionVectorToAngleDeg(dir: ICoord2D): number {
  return normalizeAngleDeg(Rad2Deg(Math.atan2(-dir.y, dir.x)));
}

const deg2RadMultiplier = Math.PI / 180;

export function Deg2Rad(degrees: number): number {
  if (degrees > 180) { degrees -= 360; }

  return degrees * deg2RadMultiplier;
}

// Signed shortest delta from `from` to `to` in (-π, π].
export function shortestAngleDelta(from: number, to: number): number {
  return Math.atan2(Math.sin(to - from), Math.cos(to - from));
}

export function ConstrainAngle(angleRad: number, angleLimit: number) {
  let angleDeg = Rad2Deg(angleRad);

  // All angles 0 to 360 are converted to be -90 to +90, to allow for constraint and easy radian conversion/usage.
  if (angleDeg > 90) {
    if (angleDeg >= 270) {
      angleDeg -= 360;
    }
    else {
      angleDeg -= 180;
    }
  }

  const constrainedAngleDeg = Math.min(Math.max(angleDeg, -angleLimit), angleLimit);

  return Deg2Rad(constrainedAngleDeg);
}

export function Clamp(me: number, min: number, max: number) {
  return Math.min(Math.max(me, min), max);
}

/** Inclusive integer clamp. When max < min, returns min. */
export function clampInt(value: number, min: number, max: number): number {
  const lo = Math.floor(min);
  const hi = Math.floor(max);
  if (hi < lo) {
    return lo;
  }
  return Clamp(Math.floor(value), lo, hi);
}

/**
 * Rounds `multiplier × value` to an integer and clamps to `[0, max]`.
 * Returns 0 when multiplier or value is not a usable non-negative scale.
 */
export function roundProductClamped(multiplier: number, value: number, max: number): number {
  if (!Number.isFinite(multiplier) || multiplier < 0 || !Number.isFinite(value) || value <= 0) {
    return 0;
  }

  const cap = Number.isFinite(max) ? Math.max(0, max) : 0;
  return Clamp(Math.round(multiplier * value), 0, cap);
}

/**
 * `ceil(count × ratio)` capped at `count`. Ratio is clamped to 0–1.
 * Returns 0 when count or ratio is not a usable positive scale.
 */
export function ceilScaledCount(count: number, ratio: number): number {
  if (!Number.isFinite(count) || count <= 0 || !Number.isFinite(ratio) || ratio <= 0) {
    return 0;
  }

  return Math.min(count, Math.ceil(count * Clamp(ratio, 0, 1)));
}

/**
 * Parses a base-10 integer from a string. Returns `fallback` when the result is not a finite value ≥ 1
 * (empty input, NaN, zero, or negatives).
 */
export function parsePositiveInt(raw: string, fallback: number): number {
  const n = Number.parseInt(raw, 10);
  return n >= 1 ? n : fallback;
}

export function Int2Bin(int: number): string {
  return (int >>> 0).toString(2);
}

export function isEven(n: number) {
  return n % 2 === 0;
}

/**
 * Rounds down to a given digit (i.e. exponent of 10).
 * @param digit - the digit after/before the decimal point to round to.
 * 3 -> 0.00n,
 * 2 -> 0.0n,
 * 1 -> 0.n,
 * 0 -> n,
 * -1 -> n0,
 * -2 -> n00,
 * -3 -> n000, etc.
 */
export function roundDown(number: number, digit: number): string {
  const powerOfTen = 10 ** Math.floor(digit);

  return (Math.floor(number * powerOfTen) / powerOfTen).toString();
}

/**
 * Get a random int between range max and min, which are both inclusive
 * @param min number must be int
 * @param max number must be int
 */
export function randomIntRange(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/** Uniform random float in the half-open interval [min, max). */
export function randomFloatRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/**
 * Cartesian offset from polar coordinates (radians, magnitude).
 * Useful for velocities and spawn offsets: `{ x: cosθ·r, y: sinθ·r }`.
 */
export function fromPolar(angleRad: number, magnitude: number): ICoord2D {
  return {
    x: Math.cos(angleRad) * magnitude,
    y: Math.sin(angleRad) * magnitude
  };
}

export function RoundDecimalNoJank(decimal: number): number {
  return Math.round((decimal + Number.EPSILON) * 100000000) / 100000000;
}

export function CreateArcVertices(startPoint: ICoord2D, angleDeg: number, segmentCount: number): ICoord2D[] {
  if (segmentCount < 1) {
    throw new Error(`Arc must have 1 or more segments/edges`);
  }

  const angleRad = Deg2Rad(angleDeg);

  const verts: ICoord2D[] = [{ x: 0, y: 0 }];
  const segAngleRad: number = angleRad / segmentCount;
  for (let i = 0; i <= segmentCount; i++) {
    const angle = segAngleRad * i;
    verts.push(GeomUtils.Rotate(startPoint, angle));
  }

  return verts;
}

export function average(...values: number[]): number {
  const sum = values.reduce((a, b) => a + b, 0);
  return (sum / values.length) || 0;
}

/** Chebyshev distance between two points (max of absolute axis deltas). */
export function chebyshevDistance(a: ICoord2D, b: ICoord2D): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

/** Returns true if two circles overlap. */
export function isCircleOverlap(
  x1: number,
  y1: number,
  r1: number,
  x2: number,
  y2: number,
  r2: number
): boolean {
  const rr = r1 + r2;
  return GetSqrMag({ x: x1, y: y1 }, { x: x2, y: y2 }) < rr * rr;
}

/**
 * Greatest common divisor of two numbers (Euclidean algorithm).
 * Useful e.g. to reduce aspect ratios to simplest form (1920×1080 → 16:9).
 * Inputs are rounded and absoluted; returns at least 1 when b is 0 to avoid division by zero.
 */
export function gcd(a: number, b: number): number {
  a = Math.round(Math.abs(a));
  b = Math.round(Math.abs(b));
  if (b === 0) { return a || 1; }
  return gcd(b, a % b);
}

/**
 * Linear interpolate from `atCenter` to `atRadius` across distance [0, radius].
 * Outside the radius, or when radius is non-positive, returns `atRadius`.
 */
export function lerpByDistance(distance: number, radius: number, atCenter: number, atRadius = 1): number {
  if (radius <= 0 || distance >= radius) { return atRadius; }

  if (distance <= 0) { return atCenter; }

  return atCenter + ((atRadius - atCenter) * (distance / radius));
}
